/**
 * Multi-wallet local registry (Web3 best practices).
 *
 * Stores **public metadata only** — never private keys, seed phrases, or session secrets.
 * On-chain balances are re-fetched via RPC / edge; cached fields are display-only.
 *
 * SSOT layers:
 * - Active signer: Phantom Browser SDK / injected (client session)
 * - Play GBUX: edge D1 per address (`/api/wallet/scopes`)
 * - Linked fleet wallets: Railway overview (Grudge ID JWT)
 * - This registry: local multi-account UX for this browser
 */
import type { SolanaWalletId } from "./solana-wallets";

export type PhantomConnectMode = "injected" | "google" | "apple";

export interface RegisteredWallet {
  /** Base58 Solana address */
  address: string;
  walletId: SolanaWalletId;
  /** Human label e.g. Phantom (Google) */
  label: string;
  /** Phantom mode when applicable */
  phantomMode?: PhantomConnectMode;
  /** Unix ms last connected / selected */
  lastSeen: number;
  /** Display cache only — revalidate from chain/edge */
  chainGbux?: number;
  chainSol?: number;
  playGbux?: number;
}

const STORAGE_KEY = "poker_grudge_wallets_v1";
const ACTIVE_KEY = "poker_grudge_wallet_active";
const MAX_WALLETS = 12;

function isValidAddress(a: string | undefined | null): a is string {
  return Boolean(a && a.length >= 32 && a.length <= 64 && !/\s/.test(a));
}

export function listRegisteredWallets(): RegisteredWallet[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RegisteredWallet[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((w) => isValidAddress(w.address))
      .slice(0, MAX_WALLETS);
  } catch {
    return [];
  }
}

function persist(list: RegisteredWallet[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_WALLETS)));
  } catch {
    /* quota / private mode */
  }
}

/** Upsert wallet after successful connect (public key only). */
export function registerWallet(
  entry: Omit<RegisteredWallet, "lastSeen"> & { lastSeen?: number },
): RegisteredWallet[] {
  if (!isValidAddress(entry.address)) return listRegisteredWallets();
  const list = listRegisteredWallets().filter(
    (w) => w.address !== entry.address,
  );
  const next: RegisteredWallet = {
    address: entry.address,
    walletId: entry.walletId,
    label: entry.label || entry.walletId,
    phantomMode: entry.phantomMode,
    lastSeen: entry.lastSeen ?? Date.now(),
    chainGbux: entry.chainGbux,
    chainSol: entry.chainSol,
    playGbux: entry.playGbux,
  };
  list.unshift(next);
  persist(list);
  setActiveWalletAddress(entry.address);
  return list;
}

export function updateWalletBalanceCache(
  address: string,
  bal: { chainGbux?: number; chainSol?: number; playGbux?: number },
): void {
  if (!isValidAddress(address)) return;
  const list = listRegisteredWallets().map((w) =>
    w.address === address
      ? {
          ...w,
          chainGbux: bal.chainGbux ?? w.chainGbux,
          chainSol: bal.chainSol ?? w.chainSol,
          playGbux: bal.playGbux ?? w.playGbux,
          lastSeen: Date.now(),
        }
      : w,
  );
  persist(list);
}

export function removeRegisteredWallet(address: string): RegisteredWallet[] {
  const list = listRegisteredWallets().filter((w) => w.address !== address);
  persist(list);
  if (getActiveWalletAddress() === address) {
    setActiveWalletAddress(list[0]?.address ?? null);
  }
  return list;
}

export function getActiveWalletAddress(): string | null {
  try {
    const a = localStorage.getItem(ACTIVE_KEY);
    return isValidAddress(a) ? a : null;
  } catch {
    return null;
  }
}

export function setActiveWalletAddress(address: string | null): void {
  try {
    if (address && isValidAddress(address)) {
      localStorage.setItem(ACTIVE_KEY, address);
    } else {
      localStorage.removeItem(ACTIVE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** Clear registry on full disconnect (optional: keep history). */
export function clearWalletRegistry(opts?: { keepHistory?: boolean }): void {
  try {
    if (!opts?.keepHistory) localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* ignore */
  }
}

export function truncateAddress(address: string, n = 4): string {
  if (!address || address.length < n * 2 + 2) return address || "";
  return `${address.slice(0, n)}…${address.slice(-n)}`;
}
